/**
 * Centralized School Data Cache System
 * Manages all school data across the application with caching and invalidation
 */

class SchoolCache {
    constructor() {
        this.schools = [];
        this.schoolMetadata = new Map(); // Store all school stats and counts
        this.lastUpdated = null;
        this.isLoading = false;
        this.error = null;
        this.listeners = new Set();
        this.cacheKey = 'amhas_schools_cache';
        this.metadataKey = 'amhas_schools_metadata';
        this.cacheVersion = '1.1';
    }

    // Subscribe to cache updates
    subscribe(callback) {
        this.listeners.add(callback);
        return () => this.listeners.delete(callback);
    }

    // Notify all listeners of cache updates
    notify() {
        this.listeners.forEach(callback => callback({
            schools: this.schools,
            isLoading: this.isLoading,
            error: this.error,
            lastUpdated: this.lastUpdated
        }));
    }

    // Load schools from API
    async loadSchools() {
        if (this.isLoading) return;

        this.isLoading = true;
        this.error = null;
        this.notify();

        try {
            // Try to load from localStorage first
            const cached = this.getFromLocalStorage();
            if (cached && this.isCacheValid(cached)) {
                // Filter out schools with invalid IDs from cache
                this.schools = (cached.schools || []).filter(school => {
                    return school && school.id && typeof school.id === 'string' && school.id.length === 24;
                });
                this.lastUpdated = cached.lastUpdated;
                this.isLoading = false;
                this.notify();
                return;
            }

            // Load from API
            const response = await fetch('/api/schools?limit=5000');
            if (!response.ok) {
                throw new Error(`Failed to load schools: ${response.status}`);
            }

            const data = await response.json();
            // Filter out schools with invalid IDs
            this.schools = (data.schools || []).filter(school => {
                // Check if school has a valid ID (MongoDB ObjectId format)
                return school && school.id && typeof school.id === 'string' && school.id.length === 24;
            });
            this.lastUpdated = new Date();

            // Preload all school metadata (stats + counts) in background
            this.preloadAllMetadata();

            this.isLoading = false;

            // Save to localStorage
            this.saveToLocalStorage();

            this.notify();
        } catch (error) {
            console.error('Error loading schools:', error);
            this.error = error.message;
            this.isLoading = false;
            this.notify();
        }
    }

    // Get schools with optional filtering
    getSchools(filters = {}) {
        let filteredSchools = [...this.schools];

        // Apply filters
        if (filters.region) {
            filteredSchools = filteredSchools.filter(school =>
                school.region.toLowerCase().includes(filters.region.toLowerCase())
            );
        }

        if (filters.district) {
            filteredSchools = filteredSchools.filter(school =>
                school.district.toLowerCase().includes(filters.district.toLowerCase())
            );
        }

        if (filters.type) {
            filteredSchools = filteredSchools.filter(school =>
                school.type.toLowerCase().includes(filters.type.toLowerCase())
            );
        }

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filteredSchools = filteredSchools.filter(school =>
                school.name.toLowerCase().includes(searchTerm) ||
                school.district.toLowerCase().includes(searchTerm) ||
                school.region.toLowerCase().includes(searchTerm) ||
                school.address.toLowerCase().includes(searchTerm)
            );
        }

        return filteredSchools;
    }

    // Search schools with advanced algorithm
    searchSchools(query, limit = 20) {
        if (!query || query.length < 1) return [];

        const searchTerm = query.toLowerCase();
        const schools = this.getSchools();

        // Score schools based on match quality
        const scoredSchools = schools.map(school => {
            let score = 0;
            const name = school.name.toLowerCase();
            const district = school.district.toLowerCase();
            const region = school.region.toLowerCase();

            // Exact name match gets highest score
            if (name === searchTerm) score += 100;
            else if (name.startsWith(searchTerm)) score += 80;
            else if (name.includes(searchTerm)) score += 60;

            // District match
            if (district.includes(searchTerm)) score += 40;

            // Region match
            if (region.includes(searchTerm)) score += 20;

            return { school, score };
        })
            .filter(item => item.score > 0)
            .sort((a, b) => {
                // Sort by score (descending), then by name (ascending)
                if (a.score !== b.score) return b.score - a.score;
                return a.school.name.localeCompare(b.school.name);
            })
            .slice(0, limit)
            .map(item => item.school);

        return scoredSchools;
    }

    // Get schools by region
    getSchoolsByRegion(region) {
        return this.schools.filter(school =>
            school.region.toLowerCase() === region.toLowerCase()
        );
    }

    // Get unique regions
    getRegions() {
        const regions = [...new Set(this.schools.map(school => school.region))];
        return regions.sort();
    }

    // Get unique districts
    getDistricts() {
        const districts = [...new Set(this.schools.map(school => school.district))];
        return districts.sort();
    }

    // Get unique school types
    getSchoolTypes() {
        const types = [...new Set(this.schools.map(school => school.type))];
        return types.sort();
    }

    // Check if cache needs refresh
    needsRefresh() {
        if (!this.lastUpdated) return true;
        const now = new Date();
        const diff = now - this.lastUpdated;
        const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
        return diff > oneHour;
    }

    // Check if school data has changed on server
    async checkForUpdates() {
        try {
            const response = await fetch('/api/schools/count');
            if (!response.ok) return false;

            const data = await response.json();
            const serverCount = data.count || 0;
            const localCount = this.schools.length;

            return serverCount !== localCount;
        } catch (error) {
            console.warn('Failed to check for school data updates:', error);
            return false;
        }
    }

    // Auto-refresh if data has changed
    async autoRefresh() {
        if (this.needsRefresh()) {
            const hasUpdates = await this.checkForUpdates();
            if (hasUpdates) {
                console.log('School data has changed, refreshing cache...');
                await this.refresh();
            } else {
                // Update timestamp to prevent frequent checks
                this.lastUpdated = new Date();
                this.saveToLocalStorage();
            }
        }
    }

    // Force refresh cache
    async refresh() {
        this.lastUpdated = null;
        await this.loadSchools();
    }

    // Local storage methods
    saveToLocalStorage() {
        try {
            const cacheData = {
                schools: this.schools,
                lastUpdated: this.lastUpdated,
                version: this.cacheVersion
            };
            localStorage.setItem(this.cacheKey, JSON.stringify(cacheData));
        } catch (error) {
            console.warn('Failed to save schools to localStorage:', error);
        }
    }

    getFromLocalStorage() {
        try {
            const cached = localStorage.getItem(this.cacheKey);
            if (!cached) return null;

            const cacheData = JSON.parse(cached);
            if (cacheData.version !== this.cacheVersion) {
                localStorage.removeItem(this.cacheKey);
                return null;
            }

            return cacheData;
        } catch (error) {
            console.warn('Failed to load schools from localStorage:', error);
            return null;
        }
    }

    isCacheValid(cached) {
        if (!cached || !cached.lastUpdated) return false;

        const now = new Date();
        const lastUpdated = new Date(cached.lastUpdated);
        const diff = now - lastUpdated;
        const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds

        return diff < oneHour;
    }

    // Clear cache
    clear() {
        this.schools = [];
        this.lastUpdated = null;
        this.error = null;
        localStorage.removeItem(this.cacheKey);
        this.notify();
    }

    // Preload all school metadata with ONE bulk query
    async preloadAllMetadata() {
        // Check if already loaded from localStorage
        if (this.schoolMetadata.size > 0) {
            console.log(`✅ Using cached metadata for ${this.schoolMetadata.size} schools`);
            return;
        }

        console.log('🔄 Preloading ALL school metadata with bulk query...');

        try {
            // ONE bulk query for all student counts
            const studentCountsResponse = await fetch('/api/students/school-counts').catch(() => null);
            const studentCounts = studentCountsResponse?.ok ? await studentCountsResponse.json() : [];

            // Create a map of student counts by school ID
            const countMap = new Map();
            studentCounts.forEach(item => {
                if (item.schoolId) {
                    countMap.set(item.schoolId, item.studentCount || 0);
                }
            });

            // For reviews, we'll initialize with zeros and let them load on-demand
            // This avoids the 503 errors from trying to fetch stats for 3,592 schools
            for (const school of this.schools) {
                this.schoolMetadata.set(school.id, {
                    stats: { averageRating: 0, reviewCount: 0 }, // Will be loaded on-demand
                    studentCount: countMap.get(school.id) || 0,
                    loadedAt: Date.now(),
                    statsLoaded: false // Flag to track if stats have been loaded
                });
            }

            console.log(`✅ Preloaded metadata for ${this.schoolMetadata.size} schools`);

            // Save to localStorage
            this.saveMetadataToLocalStorage();

        } catch (error) {
            console.warn('Failed to preload metadata:', error);
        }
    }

    // Load stats for a specific school on-demand (lazy loading)
    async loadSchoolStats(schoolId) {
        const metadata = this.schoolMetadata.get(schoolId);

        // If stats already loaded, return cached data
        if (metadata && metadata.statsLoaded) {
            return metadata.stats;
        }

        try {
            const response = await fetch(`/api/reviews/stats/${schoolId}`);
            if (response.ok) {
                const stats = await response.json();

                // Update the metadata
                this.schoolMetadata.set(schoolId, {
                    ...metadata,
                    stats,
                    statsLoaded: true,
                    loadedAt: Date.now()
                });

                // Save to localStorage
                this.saveMetadataToLocalStorage();

                return stats;
            }
        } catch (error) {
            // Return cached or default
        }

        return metadata?.stats || { averageRating: 0, reviewCount: 0 };
    }

    // Get metadata for a school (from cache)
    getSchoolMetadata(schoolId) {
        return this.schoolMetadata.get(schoolId) || {
            stats: { averageRating: 0, reviewCount: 0 },
            studentCount: 0,
            loadedAt: null
        };
    }

    // Save metadata to localStorage
    saveMetadataToLocalStorage() {
        try {
            const metadataArray = Array.from(this.schoolMetadata.entries());
            localStorage.setItem(this.metadataKey, JSON.stringify(metadataArray));
        } catch (error) {
            console.warn('Failed to save metadata to localStorage:', error);
        }
    }

    // Load metadata from localStorage
    loadMetadataFromLocalStorage() {
        try {
            const cached = localStorage.getItem(this.metadataKey);
            if (cached) {
                const metadataArray = JSON.parse(cached);
                this.schoolMetadata = new Map(metadataArray);
                console.log(`✅ Loaded metadata for ${this.schoolMetadata.size} schools from cache`);
            }
        } catch (error) {
            console.warn('Failed to load metadata from localStorage:', error);
        }
    }

    // Get cache stats
    getStats() {
        return {
            totalSchools: this.schools.length,
            metadataCached: this.schoolMetadata.size,
            lastUpdated: this.lastUpdated,
            isLoading: this.isLoading,
            error: this.error,
            regions: this.getRegions().length,
            districts: this.getDistricts().length,
            types: this.getSchoolTypes().length
        };
    }
}

// Create singleton instance
const schoolCache = new SchoolCache();

// Load metadata from localStorage on initialization
schoolCache.loadMetadataFromLocalStorage();

// Auto-load schools on initialization
schoolCache.loadSchools();

export default schoolCache;
